import React, { useCallback, useState, useMemo, useEffect, useContext } from "react";
import ReactPlayer from 'react-player';
import { Slider, Box, IconButton } from '@mui/material';

import PlayArrow from "@mui/icons-material/PlayArrow";
import Pause from "@mui/icons-material/Pause";

import { GlobalContext } from "../GlobalContext";
import {Forward10, Replay10, VolumeOff, VolumeUp} from "@mui/icons-material";

const Player = React.forwardRef(({
    isVideo,
    showTimeControls = false,
    showPlayButton = true,
    onProgress: onExternalProgress,
    onEnded: onExternalEnded,
    onPlay: onExternalPlay,
    onPause: onExternalPause,
    onError: onExternalError,
    playerBarContent,
    fullscreenMode,
    enableControls= true,
    syncing = false,
    ...props }, playerRef) => {

    const { player: currentPlayer, setPlayer } = useContext(GlobalContext);

    if (!playerRef) playerRef = React.createRef();

    const controlsStyle = {
        backgroundColor: 'var(--player-bg)',
        zIndex: '65535'
    };

    if (fullscreenMode) {
        Object.assign(controlsStyle, {
            position: 'fixed',
            paddingTop: '.5em',
            bottom: 0,
            width: '100%'
        });
    } else {
        Object.assign(controlsStyle, {
            //position: 'absolute',
        });
    }

    const playerConfig = {
        file: {
            attributes: {
                controlsList: 'nodownload noplaybackrate',
                controls: false,
            },
        }
    };

    const [playing, setPlaying] = useState(false);
    const [muted, setMuted] = useState(false);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);
    const [scrubbing, setScrubbing] = useState(false);

    const PlayerIcon = useMemo(() => playing ? Pause : PlayArrow, [playing]);
    const MuteIcon = useMemo(() => muted ? VolumeOff : VolumeUp, [muted]);

    useEffect(() => {
        if (!playerRef.current || !currentPlayer) return;

        const internalPlayer = playerRef.current?.getInternalPlayer();

        if (currentPlayer !== internalPlayer) {
            if (internalPlayer) internalPlayer.pause();
        }
    }, [playerRef, currentPlayer]);

    const onProgress = useCallback((e) => {
        //if (onExternalProgress) onExternalProgress(e); - bound to timeupdate now for better granularity
        if (scrubbing) return;

        if (playerRef.current && duration > 0) {
            setProgress(Math.round(playerRef.current.getCurrentTime() / duration * 1000) / 10);
        } else {
            //  setProgress(0);
        }
    }, [setProgress, duration, playerRef, scrubbing]);

    const onStart = useCallback(() => {
        if (playerRef.current) {
            setDuration(playerRef.current?.getDuration());

            const player = playerRef.current?.getInternalPlayer();
            if (player)
                setPlayer(player);

            onPlay();
        }
    }, [playerRef, setPlayer, setDuration]);

    const onPlay = useCallback(() => {
        setPlaying(true);
        if (onExternalPlay) onExternalPlay();
    }, [playerRef, setPlaying, onExternalPlay]);

    const onPause = useCallback(() => {
        setPlaying(false);
        if (onExternalPause) onExternalPause();
    }, [playerRef, setPlaying, onExternalPause]);

    const onEnded = useCallback((e) => {
        setPlaying(false);
        if (onExternalEnded) onExternalEnded(e);
    }, [playerRef, setPlaying, onExternalEnded]);

    const onError = useCallback((e) => {
        if(!(e instanceof DOMException)) {
            // If video is not playable just due to autoplay restrictions, do not throw any error
            if (onExternalError) onExternalError(e);
        }
    }, [playerRef, onExternalError]);

    const onPlayIcon = useCallback((e) => {
        if (!playerRef.current) return;

        const player = playerRef.current.getInternalPlayer();
        playing ? player.pause() : player.play();

        e.preventDefault();
        e.stopPropagation();

    }, [playing, playerRef]);

    const onMuteIcon = useCallback((e) => {
        if (!playerRef.current) return;
        setMuted(!muted)

        e.preventDefault();
        e.stopPropagation();
    }, [setMuted, playerRef]);

    const valueFormat = (value) => {
        //const title=`${duration} ${progress} ${duration*value/100}`;
        const curTime = duration * value / 100;
        const s = `${parseInt(curTime % 60)}`.padStart(2, '0');
        const m = parseInt((curTime / 60) % 60);

        const durS = `${parseInt(duration % 60)}`.padStart(2, '0');
        const durM = parseInt((duration / 60) % 60);

        return `${m}:${s} / ${durM}:${durS}`;
    }

    const rewind = useCallback((e) => {
        const pos = playerRef.current?.getCurrentTime();

        playerRef.current?.seekTo(pos > 10 ? pos - 10 : 0);
        e.preventDefault(); e.stopPropagation();
    });

    const forward = useCallback((e) => {
        const pos = playerRef.current?.getCurrentTime();
        const duration = playerRef.current?.getDuration();
        playerRef.current?.seekTo(Math.floor(pos + 10, duration));
        e.preventDefault(); e.stopPropagation();
    });

    return <div className="player-container">
        <ReactPlayer width='100%' height='100%'
            ref={playerRef}
            config={playerConfig}
            onProgress={onProgress}
            onStart={onStart}
            onPlay={onPlay}
            onPause={onPause}
            onEnded={onEnded}
            onError={onError}
            onTimeUpdate={onExternalProgress}
            pip={false /* TODO - prop true */}
            progressInterval={1000}
            muted={muted}
            {...props}
    />
        <div className="player-controls" style={{
            backgroundColor: 'var(--player-bg)',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
        }}>
            {(showPlayButton === true && !syncing) && <IconButton onClick={enableControls && onPlayIcon}><PlayerIcon className="controlsIcon--small" /></IconButton>}
            {(showPlayButton === true && syncing) && <IconButton onClick={enableControls && onMuteIcon}><MuteIcon className="controlsIcon--small" /></IconButton>}
            {(showPlayButton === true && !syncing) && <IconButton title="Rewind 10 seconds" onClick={enableControls && rewind}><Replay10 size="small" /></IconButton>}
            <Box sx={{ flexGrow: 1, pt: 0.5, px: 1 }} style={{ margin: 'auto', verticalAlign: 'middle' }}>
                <Slider
                    size="small"
                    aria-label="time-indicator"
                    value={progress}
                    defaultValue={0}
                    valueLabelFormat={valueFormat}
                    valueLabelDisplay='auto'
                    step={0.01}
                    height={'100%'}
                    disabled={!enableControls || syncing}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e, value) => {
                        if (!scrubbing) setScrubbing(true);
                        setProgress(value);
                    }}
                    onChangeCommitted={(_, val) => {
                        playerRef.current && playerRef.current.seekTo(val / 100, 'fraction');
                        setProgress(val);
                        setScrubbing(false);
                    }}
                />
            </Box>
            {(showPlayButton === true && !syncing) && <IconButton title="Forward 10 seconds" onClick={enableControls && forward}><Forward10 size="small" /></IconButton>}
            {playerBarContent && playerBarContent}
        </div>
    </div>
});

export default Player;
